import React, { useEffect } from 'react';
import AccountSetupCta from './AccountSetupCta';
import ConciergeOtoGate from './ConciergeOtoGate';
import DexaCreditsNote from './DexaCreditsNote';
import { getTenantDashboardUrl } from '@/lib/tenant';

const CompleteStep = ({ onComplete, answers, STORAGE_KEY, visitResult, visitType = '' }) => {
  // Get patient name from answers
  const contactInfo = answers?.['basic-contact'] || {};
  const patientName = contactInfo.name || 'Patient';
  const firstName = patientName.split(' ')[0];

  // Sync visits (e.g. WV, KS) require the patient to complete a live video
  // consult inside the Beluga patient portal before a prescription can issue.
  const isSyncVisit = visitResult?.isSyncVisit === true;


  useEffect(() => {
    // Clear any saved progress on completion
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing saved progress:', e);
    }
  }, [STORAGE_KEY]);

  const jumpto = () => {
    // After a successful submission, send the patient to the dashboard login.
    window.location.href = getTenantDashboardUrl();
  }

  return (
    // §concierge-oto: New York treatment patients see the one-time offer first
    // (flag-gated); everyone else gets the confirmation straight away.
    <ConciergeOtoGate answers={answers} visitType={visitType} visitResult={visitResult}>
    <div data-testid="complete-step" className="text-center">
      {/* Success Icon */}
      <div className="w-20 h-20 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-10 h-10 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      {/* Title */}
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        {isSyncVisit ? 'Almost there!' : 'Complete!'}
      </h2>

      {/* Subtitle */}
      <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
        Thank you, {firstName}! Your intake form has been successfully submitted.
      </p>

      {isSyncVisit && (
        <div
          data-testid="sync-visit-notice"
          className="bg-[#fff7ed] border border-[#fed7aa] rounded-2xl p-6 mb-8 max-w-md mx-auto text-left"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="w-8 h-8 bg-[#fed7aa] rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-[#c2410c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Action required: complete your video visit</h3>
              <p className="text-sm text-[#c2410c] font-medium mt-0.5">Awaiting you</p>
            </div>
          </div>
          <p className="text-gray-700 text-sm mb-2">
            Your state requires a brief live video visit with a licensed provider before a prescription can be issued.
          </p>
          <p className="text-gray-700 text-sm">
            Please log into your patient portal using the temporary credentials we just emailed you, and click <span className="font-semibold">&ldquo;Complete your visit&rdquo;</span> to schedule and conduct the video visit. Treatment cannot begin until this step is finished.
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-[#f0faf4] rounded-2xl p-6 mb-8 max-w-md mx-auto">
        <h3 className="font-semibold text-gray-900 mb-4">What happens next?</h3>
        <ul className="space-y-3 text-left">
             <li className="flex items-start gap-3">
            <div className="w-6 h-6 bg-[#2d8a5f] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs font-bold">1</span>
            </div>
            <span className="text-gray-700">
              {isSyncVisit
                ? 'Log into your patient portal and complete your live video visit'
                : 'Please login with your credentials and complete the verification'}
            </span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 bg-[#2d8a5f] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs font-bold">2</span>
            </div>
            <span className="text-gray-700">
              {isSyncVisit
                ? 'During the visit, the provider will review your intake and prescribe if appropriate'
                : 'A licensed provider will review your information within 24-48 hours'}
            </span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 bg-[#2d8a5f] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs font-bold">3</span>
            </div>
            {/* §preference-not-prescription: the provider decides the product —
                it may differ from what the patient picked at checkout. Never
                promise the SELECTED item will be dispensed. */}
            <span className="text-gray-700">
              Your provider prescribes the treatment that&rsquo;s right for you, which
              may differ from the option you selected
            </span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 bg-[#2d8a5f] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs font-bold">4</span>
            </div>
            <span className="text-gray-700">
              If a prescription is issued, it goes to our partner pharmacy and
              ships directly to your door
            </span>
          </li>
        </ul>
      </div>

      {/* Account setup (self-hides for returning users / no link) */}
      <DexaCreditsNote answers={answers} />
      <AccountSetupCta email={contactInfo.email} />

      {/* Contact Info */}
      <div className="bg-gray-50 rounded-xl p-4 max-w-md mx-auto mb-8 mt-6">
        <p className="text-sm text-gray-600">
          We&apos;ll send updates to <span className="font-medium text-gray-900">{contactInfo.email || 'your email'}</span>
        </p>
      </div>

      {/* Support */}
      <p className="text-sm text-gray-500">
        Questions? Contact us at{' '}
        <a href="mailto:support@fitpal.com" className="text-[#2d8a5f] hover:underline">
          support@fitpal.com
        </a>
      </p>

      {/* Complete Button */}
      <button
        onClick={() => jumpto()}
        className="btn-primary mt-8"
        data-testid="done-button"
      >
        {isSyncVisit ? 'Go to my patient portal' : 'Done'}
      </button>
    </div>
    </ConciergeOtoGate>
  );
};

export default CompleteStep;
